import { TalkingAvatarComponent } from './talking-avatar/talking-avatar.component';
import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, delay, EMPTY, Subscription } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

import { TBotItem } from '@core/interfaces/TBotItem';
import { AllowedRoleDirective } from '@shared/directives/allowed-role.directive';
import { HeaderComponent } from '@theme/header/header.component';

import { AgentConversationComponent } from '../../features/conversation/components/agent-conversation.component';
import { AgentConversationService } from '../../features/conversation/services/agent-conversation.service';
import { ConversationDataService } from '../../features/conversation/services/conversation-data.service';
import { NewBotModalComponent } from '../components/gingr-new-items-modals/new-bot-modal/new-bot-modal.component';
import { BotStatusBadgeComponent } from '@shared/components/bot-status-badge/bot-status-badge.component';
import { ContentShellComponent, EmptyStateComponent, ListItemComponent, PanelHeaderComponent, PanelSearchComponent, } from '@shared/components/content-panel';
import { BotsService } from '@shared/services/bots.service';
import { isNotNullAndDefined } from '@shared/utils/isNotNullAndDefined';

@Component({
  selector: 'app-bots',
  templateUrl: './bots.component.html',
  styleUrl: './bots.component.scss',
  imports: [
    ContentShellComponent,
    EmptyStateComponent,
    HeaderComponent,
    PanelHeaderComponent,
    PanelSearchComponent,
    ListItemComponent,
    MatIconButton,
    MatIcon,
    MatTooltip,
    AgentConversationComponent,
    TalkingAvatarComponent,
    BotStatusBadgeComponent,
    AllowedRoleDirective,
  ],
})
export class BotsComponent implements OnInit, AfterViewInit, OnDestroy {
  bots: TBotItem[] = [];
  selectedBot: TBotItem | null = null;
  searchQuery = '';

  private botIdFromRoute: string | null = null;
  private _subs: Subscription[] = [];

  get filteredBots(): TBotItem[] {
    const filtered = !this.searchQuery
      ? this.bots
      : this.bots.filter(bot => bot.title.toLowerCase().includes(this.searchQuery.toLowerCase()));
    return filtered.sort((a, b) => a.title.localeCompare(b.title));
  }

  constructor(
    private _botsService: BotsService,
    public dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private _agentConversationService: AgentConversationService,
    private _conversationData: ConversationDataService,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const params$ = this.route.firstChild?.params || EMPTY;
    const queryParams$ = this.route.firstChild?.queryParams || EMPTY;

    const routeChanges$ = combineLatest([params$, queryParams$]).pipe(
      tap(([params]) => {
        if (params?.id) {
          this.botIdFromRoute = params.id;
          this._setBotFromRoute();
        }
      }),
      filter(
        ([params, queryParams]) => !!params?.id && !!queryParams?.sources && !!queryParams?.action
      ),
      switchMap(([params, queryParams]) => {
        this.botIdFromRoute = params.id;
        this._setBotFromRoute();

        return this._conversationData.componentReady$.pipe(
          filter(isReady => isReady),
          take(1),
          delay(0),
          tap(() => {
            this.ngZone.run(() => {
              this._agentConversationService.newConversation({ _id: params.id } as TBotItem);
              this._agentConversationService.askQuestionToAgent({
                message: 'Create ' + queryParams.action,
                sourceIds: queryParams.sources,
              });
            });
          })
        );
      })
    );

    this._subs.push(routeChanges$.subscribe());
  }

  ngOnInit(): void {
    this._subs.push(
      this._botsService.getBotsObservable().subscribe(bots => {
        this.bots = bots;
        this._setBotFromRoute();
      })
    );
    this._botsService.fetchBots();
    this._botsService.fetchBotTypes();
  }

  private _setBotFromRoute(): void {
    if (this.botIdFromRoute && this.bots.length) {
      const bot = this.bots.find(item => item._id === this.botIdFromRoute);
      if (bot && (!this.selectedBot || bot._id !== this.selectedBot._id)) {
        this.selectedBot = bot;
      }
    }
  }

  addNewItem(): void {
    this.dialog.open(NewBotModalComponent);
  }

  hasSufix(bot: TBotItem): boolean {
    return isNotNullAndDefined(bot.sufix);
  }

  selectBot(bot: TBotItem): void {
    this.selectedBot = bot;
    this.router.navigateByUrl(`/bots/${bot._id}`);
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
